import { useRenderNotifier } from '@wui/common-library'
import { useIM } from '@wui/im'
import { CanceledError } from 'axios'
import throttle from 'lodash.throttle'
import { v4 as uuidv4 } from 'uuid'
import { computed, ref } from 'vue'

import { useStoreMediaDevices } from '@/entities/media-devices'
import { draftModel } from '@/entities/messages'
import { useRoom } from '@/entities/use-room'
import {
  extractPeaksFromBlob,
  getAudioMimeWithoutCodecs,
  getExtensionFromMimeType,
  getSupportedRecorderMimeType,
  MAX_VOICE_DURATION_MS,
  MAX_VOICE_FILE_SIZE_BYTES,
  MIN_VOICE_DURATION_MS,
  RECORDING_TIMESLICE_MS,
  useVoiceMessageStore,
  useVoicePlayer,
  VOICE_MESSAGE_FILE_PREFIX,
  VOICE_MESSAGE_SUB_TYPE,
  VOICE_RECORDING_INFO_TYPE,
  VoiceDraftSession,
  VoiceRecorderStatus,
  WAVEFORM_BARS_COUNT,
} from '@/entities/voice-message'
import { eventBus } from '@/entities/webrtc'

import { pauseAllChatMessagePlayers } from '@/shared/components/players'
import { useLocalization } from '@/shared/composables/localization'
import { getMicrophonePermissionErrorMessage } from '@/shared/composables/media-stream'
import { usePromptServer } from '@/shared/composables/web-dav'
import { ChatFile } from '@/shared/types/files'
import { Room } from '@/shared/types/store'
import { pServedIsRoom } from '@/shared/utils/uri'

type RecorderEngine = {
  pServed: string
  roomId: string
  mediaRecorder: MediaRecorder
  stream: MediaStream
  chunks: Blob[]
  mimeType: string
  audioContext: AudioContext | null
  analyser: AnalyserNode | null
  source: MediaStreamAudioSourceNode | null
  rafId: number
  durationTimer: ReturnType<typeof setInterval> | null
  recordingStartedAt: number
  accumulatedMs: number
  previewAudio: HTMLAudioElement | null
  previewUrl: string | null
  sendingFile: ChatFile | null
}

let engine: RecorderEngine | null = null
let engineListenersBound = false
const liveRecorderPServed = ref<string | null>(null)
const previewProgress = ref(0)
const isPreviewPlaying = ref(false)
let detachedPreview: { pServed: string, audio: HTMLAudioElement, url: string } | null = null

const collectBlob = (chunks: Blob[], mimeType: string): Blob | null => {
  if (!chunks.length) return null
  return new Blob(chunks, { type: mimeType })
}

const getPeakFromAnalyser = (analyser: AnalyserNode): number => {
  const data = new Uint8Array(analyser.fftSize)
  analyser.getByteTimeDomainData(data)

  let sum = 0
  for (const value of data) {
    const normalized = (value - 128) / 128
    sum += normalized * normalized
  }

  return Math.min(1, Math.sqrt(sum / data.length) * 4)
}

export const useVoiceRecorder = () => {
  const store = useVoiceMessageStore()
  const { pause: pausePlayback } = useVoicePlayer()
  const { openMicrophone, closeStream } = useStoreMediaDevices()
  const { notifyError } = useRenderNotifier()
  const { t } = useLocalization()
  const { sendExtendedInfo } = useIM()
  const { saveFile, abortSaveFile, removeFile, maxUploadFileSize } = usePromptServer()
  const roomApi = useRoom()
  const { removeDraft, findByPServed } = draftModel.useDraftMessageStore()

  const syncSession = (patch: Partial<VoiceDraftSession>) => {
    if (!engine) return

    const current = store.getSession(engine.pServed)
    if (!current) return

    store.upsertSession({
      ...current,
      ...patch,
    })
  }

  const currentDurationMs = (): number => {
    if (!engine) return 0
    if (engine.mediaRecorder.state === 'recording') {
      return engine.accumulatedMs + (performance.now() - engine.recordingStartedAt)
    }
    return engine.accumulatedMs
  }

  const stopAnalyser = () => {
    if (!engine) return
    if (engine.rafId) cancelAnimationFrame(engine.rafId)
    engine.rafId = 0
  }

  const startAnalyser = () => {
    if (!engine?.analyser) return

    const tick = () => {
      if (!engine?.analyser || engine.mediaRecorder.state !== 'recording') return

      const session = store.getSession(engine.pServed)
      const nextPeaks = [...(session?.peaks ?? []), getPeakFromAnalyser(engine.analyser)]
        .slice(-WAVEFORM_BARS_COUNT)

      syncSession({ peaks: nextPeaks, durationMs: currentDurationMs() })
      engine.rafId = requestAnimationFrame(tick)
    }

    engine.rafId = requestAnimationFrame(tick)
  }

  const clearDurationTimer = () => {
    if (!engine?.durationTimer) return
    clearInterval(engine.durationTimer)
    engine.durationTimer = null
  }

  const startDurationTimer = () => {
    if (!engine) return
    clearDurationTimer()
    engine.durationTimer = setInterval(() => {
      if (!engine) return
      const durationMs = currentDurationMs()
      syncSession({ durationMs })
      if (durationMs >= MAX_VOICE_DURATION_MS) {
        void pause(engine.pServed)
      }
    }, 200)
  }

  const revokePreview = () => {
    if (!engine) return
    if (engine.previewAudio) {
      engine.previewAudio.pause()
      engine.previewAudio.src = ''
      engine.previewAudio = null
    }
    if (engine.previewUrl) {
      URL.revokeObjectURL(engine.previewUrl)
      engine.previewUrl = null
    }
    isPreviewPlaying.value = false
    previewProgress.value = 0
  }

  const revokeDetachedPreview = () => {
    if (!detachedPreview) return
    detachedPreview.audio.pause()
    detachedPreview.audio.src = ''
    URL.revokeObjectURL(detachedPreview.url)
    detachedPreview = null
  }

  const buildPreviewFromChunks = async () => {
    if (!engine) return

    const blob = collectBlob(engine.chunks, engine.mimeType)
    if (!blob) return

    const pServed = engine.pServed
    revokePreview()
    if (!engine) return

    engine.previewUrl = URL.createObjectURL(blob)
    const audio = new Audio(engine.previewUrl)
    audio.addEventListener('timeupdate', () => {
      if (!audio.duration) return
      previewProgress.value = audio.currentTime / audio.duration
    })
    audio.addEventListener('ended', () => {
      isPreviewPlaying.value = false
      previewProgress.value = 1
    })
    engine.previewAudio = audio
    syncSession({ blob, durationMs: currentDurationMs() })

    try {
      const { peaks } = await extractPeaksFromBlob(blob)
      if (!engine || engine.pServed !== pServed) return
      if (engine.mediaRecorder.state === 'recording') return

      syncSession({ peaks })
    } catch {
      return
    }
  }

  const sendRecordingSignal = throttle((pServed: string, state: 'start' | 'stop') => {
    if (pServedIsRoom(pServed)) return

    sendExtendedInfo({
      to: pServed,
      extendedParams: {
        type: VOICE_RECORDING_INFO_TYPE,
        state,
      },
    }).catch(() => undefined)
  }, 1000)

  const releaseMedia = () => {
    if (!engine) return

    stopAnalyser()
    clearDurationTimer()
    sendRecordingSignal(engine.pServed, 'stop')
    sendRecordingSignal.flush()

    if (engine.mediaRecorder.state !== 'inactive') {
      engine.mediaRecorder.stop()
    }

    engine.source?.disconnect()
    void engine.audioContext?.close()
    closeStream(engine.stream)
    revokePreview()
    liveRecorderPServed.value = null
  }

  const pause = async (pServed: string) => {
    if (!engine || engine.pServed !== pServed) return
    if (engine.mediaRecorder.state !== 'recording') return

    engine.accumulatedMs = currentDurationMs()
    engine.mediaRecorder.pause()
    stopAnalyser()
    clearDurationTimer()
    sendRecordingSignal(pServed, 'stop')

    await new Promise<void>((resolve) => {
      if (!engine) {
        resolve()
        return
      }
      engine.mediaRecorder.addEventListener('dataavailable', () => resolve(), { once: true })
      engine.mediaRecorder.requestData()
    })

    if (!engine || engine.pServed !== pServed) return

    syncSession({
      status: VoiceRecorderStatus.paused,
      durationMs: engine.accumulatedMs,
    })
    await buildPreviewFromChunks()

    const session = store.getSession(pServed)
    if (session) store.persistDraft(session)
  }

  const discard = (pServed: string, removeUploadedFile = true) => {
    if (engine?.pServed === pServed) {
      if (engine.sendingFile) {
        abortSaveFile(engine.sendingFile.file)
        if (removeUploadedFile && engine.sendingFile.isLoaded.value) {
          void removeFile(engine.sendingFile.name)
        }
      }
      releaseMedia()
      engine = null
    }

    store.removeSession(pServed)
    isPreviewPlaying.value = false
    previewProgress.value = 0
    revokeDetachedPreview()
  }

  const start = async (room: Room) => {
    const pServed = room.pServedUser
    if (engine?.pServed === pServed) return

    if (engine && engine.pServed !== pServed) {
      await pause(engine.pServed)
      releaseMedia()
      engine = null
    }

    const restored = await store.restoreDraft(pServed)
    if (restored) return

    pauseAllChatMessagePlayers()
    pausePlayback()

    try {
      const stream = await openMicrophone()
      const mimeType = getSupportedRecorderMimeType()
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      const audioContext = new AudioContext()
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      await audioContext.resume()

      engine = {
        pServed,
        roomId: room.id,
        mediaRecorder,
        stream,
        chunks: [],
        mimeType: mediaRecorder.mimeType || mimeType,
        audioContext,
        analyser,
        source,
        rafId: 0,
        durationTimer: null,
        recordingStartedAt: performance.now(),
        accumulatedMs: 0,
        previewAudio: null,
        previewUrl: null,
        sendingFile: null,
      }

      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data.size) engine?.chunks.push(event.data)
      })

      mediaRecorder.start(RECORDING_TIMESLICE_MS)
      liveRecorderPServed.value = pServed
      store.upsertSession({
        pServed,
        roomId: room.id,
        status: VoiceRecorderStatus.recording,
        durationMs: 0,
        mimeType: engine.mimeType,
        peaks: [],
        blob: null,
      })
      startAnalyser()
      startDurationTimer()
      sendRecordingSignal(pServed, 'start')
    } catch (error) {
      notifyError(getMicrophonePermissionErrorMessage(error))
    }
  }

  const resume = async (pServed: string) => {
    if (!engine || engine.pServed !== pServed) {
      notifyError(t('chat.voiceMessageCannotResume'))
      return
    }
    if (engine.mediaRecorder.state !== 'paused') return

    engine.previewAudio?.pause()
    isPreviewPlaying.value = false
    engine.recordingStartedAt = performance.now()
    engine.mediaRecorder.resume()
    syncSession({ status: VoiceRecorderStatus.recording })
    startAnalyser()
    startDurationTimer()
    sendRecordingSignal(pServed, 'start')
  }

  const ensurePreviewAudio = (pServed: string): HTMLAudioElement | null => {
    const session = store.getSession(pServed)
    if (engine?.pServed === pServed) {
      if (!engine.previewAudio) buildPreviewFromChunks()
      return engine.previewAudio
    }

    if (detachedPreview?.pServed === pServed) return detachedPreview.audio
    if (!session?.blob) return null

    revokeDetachedPreview()
    const url = URL.createObjectURL(session.blob)
    const audio = new Audio(url)
    audio.addEventListener('timeupdate', () => {
      if (!audio.duration) return
      previewProgress.value = audio.currentTime / audio.duration
    })
    audio.addEventListener('ended', () => {
      isPreviewPlaying.value = false
      previewProgress.value = 1
    })
    detachedPreview = { pServed, audio, url }
    return audio
  }

  const seekPreview = (pServed: string, ratio: number) => {
    const audio = engine?.pServed === pServed
      ? engine.previewAudio
      : detachedPreview?.pServed === pServed
        ? detachedPreview.audio
        : null
    if (!audio) return
    const duration = audio.duration
    if (!Number.isFinite(duration) || !duration) {
      audio.addEventListener('loadedmetadata', () => {
        audio.currentTime = audio.duration * ratio
        previewProgress.value = ratio
      }, { once: true })
      return
    }
    audio.currentTime = duration * ratio
    previewProgress.value = ratio
  }

  const togglePreview = async (pServed: string) => {
    const audio = ensurePreviewAudio(pServed)
    if (!audio) return

    if (audio.paused) {
      pauseAllChatMessagePlayers()
      pausePlayback()
      await audio.play()
      isPreviewPlaying.value = true
      return
    }

    audio.pause()
    isPreviewPlaying.value = false
  }

  const send = async (room: Room) => {
    const pServed = room.pServedUser
    const session = store.getSession(pServed)
    if (!session) return

    if (engine?.pServed === pServed && engine.mediaRecorder.state === 'recording') {
      await pause(pServed)
    }

    const durationMs = store.getSession(pServed)?.durationMs ?? 0
    if (durationMs < MIN_VOICE_DURATION_MS) {
      notifyError(t('chat.voiceMessageTooShort'))
      return
    }

    const blob = engine?.pServed === pServed
      ? collectBlob(engine.chunks, engine.mimeType)
      : session.blob
    if (!blob) return

    const limit = Math.min(MAX_VOICE_FILE_SIZE_BYTES, maxUploadFileSize())
    if (blob.size > limit) {
      notifyError(t('chat.voiceMessageLimitExceeded'))
      return
    }

    const mimeType = (engine?.pServed === pServed ? engine.mimeType : session.mimeType) || blob.type
    const audioMime = getAudioMimeWithoutCodecs(mimeType)
    const extension = getExtensionFromMimeType(mimeType)
    const file = new File([blob], `${VOICE_MESSAGE_FILE_PREFIX}.${extension}`, {
      type: audioMime,
    })
    const chatFile: ChatFile = {
      file,
      name: `${uuidv4()}.${extension}`,
      isLoaded: ref(false),
      progress: ref(0),
      isError: ref(false),
    }

    if (engine?.pServed === pServed) {
      engine.sendingFile = chatFile
    }
    store.upsertSession({
      ...store.getSession(pServed)!,
      status: VoiceRecorderStatus.sending,
      blob,
      mimeType,
    })

    try {
      await saveFile(file, '', chatFile.name)
      chatFile.isLoaded.value = true

      const draft = findByPServed(pServed)
      const quotedEventId = draft.orElse()?.quotedEventId
      const quotedText = draft.orElse()?.quotedText

      await roomApi.send('', room, [chatFile], quotedEventId, undefined, quotedText, VOICE_MESSAGE_SUB_TYPE)
      removeDraft(pServed)
      discard(pServed, false)
    } catch (error) {
      if (engine?.pServed === pServed) engine.sendingFile = null
      store.upsertSession({
        ...store.getSession(pServed)!,
        status: VoiceRecorderStatus.paused,
        blob,
        mimeType,
      })

      if (error instanceof CanceledError) return

      const message = error instanceof Error ? error.message : t('chat.voiceMessageLimitExceeded')
      notifyError(message)
    }
  }

  const cancelSend = (pServed: string) => {
    const session = store.getSession(pServed)
    const sendingFile = engine?.pServed === pServed ? engine.sendingFile : null
    if (!session || session.status !== VoiceRecorderStatus.sending) return

    if (sendingFile) {
      abortSaveFile(sendingFile.file)
      if (engine?.pServed === pServed) engine.sendingFile = null
    }

    store.upsertSession({
      ...session,
      status: VoiceRecorderStatus.paused,
    })
  }

  if (!engineListenersBound) {
    engineListenersBound = true
    eventBus.on('incomingCall', () => {
      if (engine) void pause(engine.pServed)
    })
    window.addEventListener('offline', () => {
      if (engine) void pause(engine.pServed)
    })
  }

  const sessionFor = (pServed: string) => computed(() => store.getSession(pServed))

  return {
    start,
    pause,
    resume,
    discard,
    send,
    cancelSend,
    togglePreview,
    seekPreview,
    sessionFor,
    previewProgress,
    isPreviewPlaying,
    liveRecorderPServed,
    restoreDraft: store.restoreDraft,
  }
}
